import { useState, useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { analytics } from "MVVM/Analytics";
import { UserSearchViewModel } from "./ViewModel";
import { ISearchItem, NavSearchBar } from "../SearchBar/NavSearchBar";
import { AllstarUser } from "MVVM/State";

interface IUser {
  _id: string;
  id: string;
  avatar: string;
  avatarUrl: string;
  username: string;
  discord: {
    id: string;
  };
}

interface IProPlayer {
  id: string;
  name: string;
  avatar?: string;
  game?: string;
  gameIdentifier?: string;
}

const UserSearch = () => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [proPlayers, setProPlayers] = useState<IProPlayer[]>([]);
  const history = useHistory();
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { userSearchQuery, loading, proPlayerSearchQuery, proPlayerLoading } =
    UserSearchViewModel();
  const allstarUser = useRecoilValue(AllstarUser);
  const [currentQuery, setCurrentQuery] = useState("");

  const transformUserToSearchItem = (user: IUser): ISearchItem => ({
    id: user.id,
    name: user.username,
    image: user.avatarUrl || user.avatar,
  });

  const transformProPlayerToSearchItem = (player: IProPlayer): ISearchItem => ({
    id: player.gameIdentifier || player.id,
    name: player.name,
    image: player.avatar || "",
    isProPlayer: true,
    game: player.game,
  });

  const searchUsers = useCallback(
    (query: string) => {
      setCurrentQuery(query);

      if (!query.trim()) {
        setUsers([]);
        setProPlayers([]);
        return;
      }

      userSearchQuery({ variables: { count: 10, query } })
        .then((res) => setUsers(res.data?.userSearch ?? []))
        .catch(() => setUsers([]));

      proPlayerSearchQuery({ variables: { count: 3, query } })
        .then((res) => setProPlayers(res.data?.proPlayerSearch ?? []))
        .catch(() => setProPlayers([]));
    },
    [userSearchQuery, proPlayerSearchQuery],
  );

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debouncedSearchTimeoutRef.current)
        clearTimeout(debouncedSearchTimeoutRef.current);

      debouncedSearchTimeoutRef.current = setTimeout(() => {
        searchUsers(query);
      }, 300);
    },
    [searchUsers],
  );

  useEffect(() => {
    return () => {
      if (debouncedSearchTimeoutRef.current) {
        clearTimeout(debouncedSearchTimeoutRef.current);
      }
    };
  }, []);

  const mergedItems = [
    ...proPlayers.map(transformProPlayerToSearchItem),
    ...users.map(transformUserToSearchItem),
  ];

  const onSelect = (item: ISearchItem) => {
    if (item.isProPlayer) {
      analytics.track("User Search - Click", {
        user: item.id,
        reason: "pro_player_search",
        search: currentQuery,
        following: false,
      });

      analytics.track("Button - Click", {
        location: "userSearch",
        locationIdentifier: "userSearchProPlayer",
        source: "website",
      });

      history.push(`/u/${item.id}`);
      return;
    }

    const followingIds = allstarUser?.followers?.following;
    const isFollowing = followingIds
      ? followingIds.map((x: any) => x?._id)?.includes(item.id)
      : false;

    analytics.track("User Search - Click", {
      user: item.id,
      reason: "search",
      search: currentQuery,
      following: isFollowing,
    });

    analytics.track("Button - Click", {
      location: "userSearch",
      locationIdentifier: "userSearchProfile",
      source: "website",
    });

    const location =
      !item.name || item.name === "User"
        ? `/profile?user=${item.id}`
        : `/u/${item.name}`;

    history.push(location);
  };

  const onFocus = () => {
    analytics.track("Search - Focused", {
      location: "user_search",
      locationIdentifier: "userSearch",
    });
  };

  return (
    <>
      <NavSearchBar
        items={mergedItems}
        loading={loading || proPlayerLoading}
        onSelect={onSelect}
        onFocus={onFocus}
        debouncedSearch={debouncedSearch}
        placeholder="Search for an Allstar Player..."
      />
    </>
  );
};

export { UserSearch };
